// FILE: lib/seo/slug.ts
// ═══════════════════════════════════════════════════════════════
// SEO Slug utilities — phân biệt Landing (taxonomy) vs Detail (entity)
// ═══════════════════════════════════════════════════════════════

/**
 * Detail URL format: {slug-mo-ta}-{8-hex-suffix}
 * VD: "de-thi-tin-dung-vcb-2026-5b01993a"
 *              slug phần mô tả ──↑     ↑── 8 ký tự hex đầu của UUID
 *
 * Landing URL format: slug taxonomy ghép từ tên category
 * VD: "ngan-hang-vietcombank-giao-dich-vien"
 *
 * Cách phân biệt: Detail LUÔN có suffix 8 ký tự hex ở cuối.
 * Landing KHÔNG BAO GIỜ có suffix match pattern này.
 */

const DETAIL_SUFFIX_REGEX = /^[a-f0-9]{8}$/;

/**
 * Kiểm tra xem slug có phải URL detail không
 * @returns true nếu là detail URL (có 8-hex suffix ở cuối)
 */
export function isDetailSlug(slug: string): boolean {
  const parts = slug.split("-");
  if (parts.length < 2) return false;
  const last = parts[parts.length - 1];
  return DETAIL_SUFFIX_REGEX.test(last);
}

/**
 * Extract 8-hex suffix từ detail slug
 * VD: "de-thi-vcb-2026-5b01993a" → "5b01993a"
 * @returns suffix hoặc null nếu không phải detail slug
 */
export function extractDetailSuffix(slug: string): string | null {
  const parts = slug.split("-");
  if (parts.length < 2) return null;
  const last = parts[parts.length - 1];
  return DETAIL_SUFFIX_REGEX.test(last) ? last : null;
}

/**
 * Kiểm tra xem slug có phải UUID full không
 * VD: "5e5a88a3-50fd-4d8b-86fe-54118e6b806f"
 */
export function isFullUUID(slug: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
}

// ═══════════════════════════════════════════════════════════════
// LANDING SLUG PARSING
// ═══════════════════════════════════════════════════════════════

/**
 * Các domain slugs (tier 1 của taxonomy) — dùng để detect slug bắt đầu bằng domain nào.
 * Danh sách cứng để tránh query DB mỗi lần parse (optimize performance).
 * Nếu thêm domain mới → cập nhật ở đây + backfill DB.
 */
// ⚠️ Khi thêm domain mới trong bảng categories → BẮT BUỘC thêm slug vào đây + deploy.
// Quên = toàn bộ landing page của domain mới sẽ 404.
export const DOMAIN_SLUGS = [
  "ngan-hang",
  "cong-chuc-vien-chuc",
  "cong-ty-tap-doan",
  "chung-khoan",
  "tuyen-sinh-dai-hoc",
  "chung-chi-hoc-tap",
  "chung-chi-nghe-nghiep",
  "chung-chi-tieng-anh",
  "dai-hoc-cao-dang",
] as const;

export type DomainSlug = (typeof DOMAIN_SLUGS)[number];

/**
 * Parse landing slug → tách domain và phần còn lại
 * 
 * VD:
 * - "ngan-hang" → { domainSlug: "ngan-hang", remainder: "", remainderSegments: [] }
 * - "ngan-hang-vietcombank" → { domainSlug: "ngan-hang", remainder: "vietcombank", remainderSegments: ["vietcombank"] }
 * - "ngan-hang-vietcombank-giao-dich-vien" → { domainSlug: "ngan-hang", remainder: "vietcombank-giao-dich-vien", remainderSegments: ["vietcombank", "giao-dich-vien"] }
 * 
 * @returns null nếu slug không bắt đầu bằng domain nào
 */
export interface ParsedLandingSlug {
  /** Slug của domain (VD: "ngan-hang") — null nếu slug không bắt đầu bằng domain */
  domainSlug: string | null;
  /** Phần còn lại sau khi bỏ domain (hoặc full slug nếu không có domain) */
  remainder: string;
  /** Các segment sau domain */
  remainderSegments: string[];
}

/**
 * Parse landing slug → tách domain (nếu có) và phần còn lại.
 * 
 * Hỗ trợ 2 dạng:
 * - Có domain prefix: "ngan-hang", "ngan-hang-vietcombank", "ngan-hang-vietcombank-tin-dung"
 * - KHÔNG có domain prefix: "vietcombank", "tin-dung", "vietcombank-tin-dung"
 * 
 * @returns ParsedLandingSlug — domainSlug có thể null nếu slug không bắt đầu bằng domain
 */
export function parseLandingSlug(slug: string): ParsedLandingSlug {
  // Thử match domain trước (longest-prefix)
  const sortedDomains = [...DOMAIN_SLUGS].sort((a, b) => b.length - a.length);
  for (const domain of sortedDomains) {
    if (slug === domain) {
      return { domainSlug: domain, remainder: "", remainderSegments: [] };
    }
    if (slug.startsWith(domain + "-")) {
      const remainder = slug.slice(domain.length + 1);
      return {
        domainSlug: domain,
        remainder,
        remainderSegments: remainder ? remainder.split("-") : [],
      };
    }
  }
  // Không match domain → toàn bộ slug là remainder
  return {
    domainSlug: null,
    remainder: slug,
    remainderSegments: slug.split("-"),
  };
}

// ═══════════════════════════════════════════════════════════════
// URL BUILDERS
// ═══════════════════════════════════════════════════════════════

/**
 * Build landing URL từ categories
 * VD: buildLandingSlug({ domain: "ngan-hang", org: "vietcombank" }) → "ngan-hang-vietcombank"
 */
export function buildLandingSlug(parts: {
  domain: string;
  org?: string;
  position?: string;
}): string {
  const segments = [parts.domain];
  if (parts.org) segments.push(parts.org);
  if (parts.position) segments.push(parts.position);
  return segments.join("-");
}

/**
 * Build URL đầy đủ cho landing page
 * VD: buildLandingUrl("exams", { domain: "ngan-hang", org: "vietcombank" })
 *     → "/exams/ngan-hang-vietcombank"
 */
export function buildLandingUrl(
  type: "exams" | "paths" | "reviews",
  parts: { domain: string; org?: string; position?: string }
): string {
  return `/${type}/${buildLandingSlug(parts)}`;
}

/**
 * Build URL detail cho exam/path/review
 * Dùng slug có sẵn trong DB (đã có 8-hex suffix).
 * Nếu slug rỗng → fallback dùng UUID full.
 */
export function buildDetailUrl(
  type: "exams" | "paths" | "reviews",
  entity: { slug: string | null; id: string }
): string {
  const slugPart = entity.slug || entity.id;
  return `/${type}/${slugPart}`;
}